import { ButtonBase, type ButtonBaseProps } from '@mui/material';
import { forwardRef } from 'react';
import { SAND } from '@/theme/theme';

/**
 * The header's control: a white pill with a hairline border (DESIGN-SPEC §2).
 * `iconOnly` makes it a 36px circle for a lone icon; the rest take an icon
 * and a label at 6px 14px.
 *
 * A ButtonBase, so it stays a real button for the keyboard and the tests, and
 * so Tooltip and Menu anchor to it like any other.
 */
export const Pill = forwardRef<HTMLButtonElement, ButtonBaseProps & { iconOnly?: boolean }>(function Pill(
  { iconOnly = false, sx, children, ...rest },
  ref,
) {
  return (
    <ButtonBase
      ref={ref}
      {...rest}
      sx={[
        {
          border: `1px solid ${SAND.field}`,
          backgroundColor: '#ffffff',
          borderRadius: 999,
          fontWeight: 600,
          fontSize: 14,
          fontFamily: 'inherit',
          color: SAND.inkSoft,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          whiteSpace: 'nowrap',
          // A header control never squashes; the brand beside it gives way instead.
          flexShrink: 0,
          lineHeight: 1.45,
          transition: 'background-color 120ms',
          '&:hover': { backgroundColor: SAND.rowHover },
          '&.Mui-focusVisible': { boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.tint}` },
          '& .MuiSvgIcon-root': { fontSize: 20, color: SAND.muted },
        },
        iconOnly
          ? { width: 36, height: 36, justifyContent: 'center', padding: 0 }
          : { paddingBlock: '6px', paddingInline: '14px', minHeight: 36 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </ButtonBase>
  );
});
